<template>
  <q-page>
    <q-dialog v-model="deviationDelayFormModal.state.show" :persistent="false">
      <DeviationDelayFormModal
        show-event-input
        v-if="deviationDelayFormModal.state.show"
      />
    </q-dialog>
    <q-dialog v-model="deviationCrewDeleteModal.state.show" :persistent="false">
      <DeviationDeleteModal v-if="deviationCrewDeleteModal.state.show" />
    </q-dialog>
    <q-dialog v-model="deviationDelayHideModal.state.show" :persistent="false">
      <DeviationDelayHideModal
        show-event-input
        v-if="deviationDelayHideModal.state.show"
      />
    </q-dialog>
    <DeviationDelayTable
      :addColumns="addColumns"
      :loading="loading"
      height="calc(100vh - 50px - 68px)"
      :deviation-delays="data"
    >
      <template v-slot:top-left-buttons>
        <h6 class="q-ma-none">Förseningar</h6>
        <q-btn
          v-if="can('deviationManagement.deviationDelay.create')"
          icon-right="mdi-plus-circle-outline"
          class="q-ml-md"
          label="Lägg till"
          color="primary"
          @click="() => deviationDelayFormModal.openModal({ mode: 'create' })"
        />
      </template>

      <template v-slot:top-right-buttons>
        <DeviationFilterModal deviation-type="deviationDelay" />

        <q-btn-dropdown
          label="Filter banor"
          class="q-ml-md"
          outline
          color="grey-7"
        >
          <template #label>
            <q-badge color="primary" floating>
              {{ filterTrackCount }}
            </q-badge>
          </template>
          <q-list dense>
            <q-item-label class="q-py-xs bg-grey-2" header
              >Avmarkera/markera alla</q-item-label
            >
            <q-separator />
            <q-item clickable tag="label">
              <q-item-section avatar>
                <q-checkbox
                  :indeterminate-value="null"
                  :model-value="tracksSelected"
                  @update:model-value="(v) => onTrackGroupUpdate(v)"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label>Markera alla banor</q-item-label>
              </q-item-section>
            </q-item>
            <q-separator />
            <q-item-label class="q-py-xs bg-grey-2" header>Banor</q-item-label>
            <q-separator />
            <q-item tag="label" clickable v-for="(col, $i) of tracks" :key="$i">
              <q-item-section avatar>
                <q-checkbox v-model="col.visible" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ col.label }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </template>
    </DeviationDelayTable>
  </q-page>
</template>

<script lang="ts">
import { computed, defineComponent, provide, ref, watch } from 'vue'
import DeviationDelayTable from '@/components/deviation/deviation-delay/DeviationDelayTable.vue'
import { useDeviationDelay } from '@/composable/useDeviationDelay'
import { useModal } from '@/composable/useModal'
import DeviationDeleteModal from '@/components/deviation/DeviationDeleteModal.vue'
import DeviationDelayFormModal from '@/components/deviation/deviation-delay/DeviationDelayFormModal.vue'
import DeviationFilterModal from '@/components/deviation/DeviationFilterModal.vue'
import { useProfile } from '@/composable/useProfile'
import { addColumns } from '@/common/deviationAddColumns'
import DeviationDelayHideModal from '@/components/deviation/deviation-delay/DeviationDelayHideModal.vue'
import { useDeviationDelayProposal } from '@/composable/useDeviationDelayProposal'
import orderBy from 'lodash.orderby'
import { getTracks } from '@/api/lupp/getTracks'
import { LuppTrack } from '@/types/lupp-track'
import { useUserSettings } from '@/composable/useUserSettings'
import { socket } from '@/services/socket'
import { areIntervalsOverlapping } from 'date-fns'

export default defineComponent({
  name: 'DelayDeviations',

  components: {
    DeviationDelayTable,
    DeviationDeleteModal,
    DeviationDelayFormModal,
    DeviationFilterModal,
    DeviationDelayHideModal,
  },

  setup() {
    const { can, currentProject } = useProfile()
    const {
      data: delayDeviations,
      loading: loadingStateDeviationDelay,
      fetchAll,
      period,
    } = useDeviationDelay()
    const {
      fetchAll: fetchAllProposals,
      data: deviationDelayProposals,
      loading: loadingStateDeviationDelayProposal,
    } = useDeviationDelayProposal()
    const deviationDelayHideModal = useModal()
    const deviationDelayFormModal = useModal()
    const deviationCrewDeleteModal = useModal()

    provide('deviation-delay-form-modal', deviationDelayFormModal)
    provide('deviation-delete-modal', deviationCrewDeleteModal)
    provide('deviation-delay-hide-modal', deviationDelayHideModal)

    socket.instance?.on(
      'delay-import-notification',
      (data: { startDate: string; endDate: string; project: string }) => {
        if (data.project !== currentProject.value?.name) return
        const overlapping = areIntervalsOverlapping(
          {
            start: new Date(period.value.from),
            end: new Date(period.value.to),
          },
          { start: new Date(data.startDate), end: new Date(data.endDate) }
        )

        if (!overlapping) return

        fetchAllProposals(period.value)
      }
    )

    const project = currentProject.value?.name as string
    const { settings } = useUserSettings()
    const localstorageBanor = settings.value[project].delay.tracks

    const setBanor = (luppTracks: LuppTrack[]) => {
      const banor = orderBy(
        [...new Set(luppTracks.map((x) => x.description))],
        (x) => x,
        ['asc']
      )
      tracks.value = banor
        .map((label) => {
          const bana = localstorageBanor.find((x) => x.label === label)
          return {
            label,
            visible: bana ? bana.visible : true,
          }
        })
        .filter((x) => x.label !== 'Göteborg – Halmstad')
    }

    const fetchTracks = async () => {
      const { data } = await getTracks()
      setBanor(data)
    }

    fetchTracks()
    fetchAllProposals(period.value)
    fetchAll(period.value)

    const loading = computed(() => {
      return (
        loadingStateDeviationDelayProposal.value.getAll ||
        loadingStateDeviationDelay.value.getAll
      )
    })

    interface TrackItem {
      label: string
      visible: boolean
    }

    const tracksSelected = ref<boolean | null>(false)

    const tracks = ref<TrackItem[]>([])

    watch(
      () => tracks.value,
      (value) => {
        if (value.every((x) => x.visible)) {
          tracksSelected.value = true
        } else if (value.every((x) => !x.visible)) {
          tracksSelected.value = false
        } else {
          tracksSelected.value = null
        }
      },
      {
        deep: true,
        immediate: true,
      }
    )

    const onTrackGroupUpdate = (value: boolean) => {
      tracks.value.forEach((x) => {
        x.visible = value
      })
    }

    const data = computed(() => {
      const delays = orderBy(
        delayDeviations.value,
        ['delayAdvertisedTime'],
        ['desc']
      )
      return [
        ...orderBy(
          deviationDelayProposals.value,
          ['delayPlannedTime'],
          ['desc']
        ),
        ...delays.filter(
          (x) =>
            !x.eventFlags.some((x) => {
              return x.flag === 'AUTO_DELAY'
            })
        ),
        ...delays.filter((x) =>
          x.eventFlags.some((x) => {
            return x.flag === 'AUTO_DELAY'
          })
        ),
      ].filter((x) => {
        return tracks.value.some((t) => t.label === x.bana && t.visible)
      })
    })

    const filterTrackCount = computed(() => {
      return tracks.value.filter((x) => x.visible).length
    })

    watch(
      () => tracks.value,
      (value) => {
        settings.value[project].delay.tracks = value
      }
    )

    return {
      onTrackGroupUpdate,
      tracksSelected,
      tracks,
      addColumns,
      data,
      loading,
      deviationDelayFormModal,
      deviationCrewDeleteModal,
      can,
      deviationDelayHideModal,
      filterTrackCount,
    }
  },
})
</script>
